import React from 'react';
import { cn } from '../../lib/utils';
import { Separator } from '../ui/separator';
import { type LayoutConfig, type SidebarVariant, type SidebarCollapsible, type SidebarSide } from '../../hooks/use-layout-config';

interface LayoutControlsProps {
  config: LayoutConfig;
  onChange: (config: LayoutConfig) => void;
}

// ── Layout preview illustrations ──────────────────────────────────────────────
const DefaultPreview: React.FC = () => (
  <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-sm border border-border bg-muted/30">
    <div className="h-full w-7 rounded-sm border-r border-border bg-muted/60" />
    <div className="flex flex-1 flex-col gap-0.5 p-1">
      <div className="h-1.5 w-full rounded-sm bg-muted" />
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
  </div>
);

const FloatingPreview: React.FC = () => (
  <div className="relative flex h-9 w-full overflow-hidden rounded-sm border border-border bg-muted/30">
    <div className="absolute left-1 top-1 bottom-1 w-6 rounded-sm border border-border bg-muted/80 shadow-sm" />
    <div className="ml-8 flex flex-1 flex-col gap-0.5 p-1">
      <div className="h-1.5 w-full rounded-sm bg-muted" />
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
  </div>
);

const InsetPreview: React.FC = () => (
  <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-sm border border-border bg-muted/50 p-0.5">
    <div className="h-full w-6 rounded-sm bg-muted/80 border border-border" />
    <div className="flex flex-1 flex-col gap-0.5 rounded-sm border border-border bg-background p-1">
      <div className="h-1.5 w-full rounded-sm bg-muted" />
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
  </div>
);

const OffCanvasPreview: React.FC = () => (
  <div className="relative flex h-9 w-full overflow-hidden rounded-sm border border-border bg-muted/30">
    <div className="flex h-full w-full flex-col gap-0.5 p-1.5">
      <div className="flex gap-0.5">
        <div className="h-2 w-2 rounded-sm bg-muted-foreground/30" />
        <div className="h-2 flex-1 rounded-sm bg-muted" />
      </div>
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
    {/* hamburger indicator */}
    <div className="absolute left-1 top-1 flex flex-col gap-0.5">
      <div className="h-px w-3 bg-muted-foreground/50" />
      <div className="h-px w-3 bg-muted-foreground/50" />
      <div className="h-px w-3 bg-muted-foreground/50" />
    </div>
  </div>
);

const IconPreview: React.FC = () => (
  <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-sm border border-border bg-muted/30">
    <div className="flex h-full w-5 flex-col items-center gap-0.5 bg-muted/60 border-r border-border pt-1">
      <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
      <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
      <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
    </div>
    <div className="flex flex-1 flex-col gap-0.5 p-1">
      <div className="h-1.5 w-full rounded-sm bg-muted" />
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
  </div>
);

const NonePreview: React.FC = () => (
  <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-sm border border-border bg-muted/30">
    <div className="h-full w-8 rounded-sm border-r border-border bg-muted/60" />
    <div className="flex flex-1 flex-col gap-0.5 p-1">
      <div className="h-1.5 w-full rounded-sm bg-muted" />
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
  </div>
);

const LeftPreview: React.FC = () => (
  <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-sm border border-border bg-muted/30">
    <div className="h-full w-7 rounded-sm border-r border-border bg-muted/60" />
    <div className="flex flex-1 flex-col gap-0.5 p-1">
      <div className="h-1.5 w-full rounded-sm bg-muted" />
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
  </div>
);

const RightPreview: React.FC = () => (
  <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-sm border border-border bg-muted/30">
    <div className="flex flex-1 flex-col gap-0.5 p-1">
      <div className="h-1.5 w-full rounded-sm bg-muted" />
      <div className="h-1.5 w-3/4 rounded-sm bg-muted" />
    </div>
    <div className="h-full w-7 rounded-sm border-l border-border bg-muted/60" />
  </div>
);

// ── Generic selectable card ───────────────────────────────────────────────────
interface OptionCardProps {
  label: string;
  description?: string;
  selected: boolean;
  onClick: () => void;
  preview: React.ReactNode;
}

const OptionCard: React.FC<OptionCardProps> = ({ label, description, selected, onClick, preview }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex flex-col gap-1.5 rounded-lg border-2 bg-background dark:bg-muted/30 p-2 text-left transition-all hover:bg-muted/30 dark:hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      selected ? 'border-foreground' : 'border-border dark:border-muted-foreground/20'
    )}
  >
    {preview}
    <p className="text-xs font-medium leading-none text-foreground">{label}</p>
    {description && (
      <p className="text-[11px] text-muted-foreground leading-tight">{description}</p>
    )}
  </button>
);

// ── Main component ────────────────────────────────────────────────────────────
export const LayoutControls: React.FC<LayoutControlsProps> = ({ config, onChange }) => {
  const update = (partial: Partial<LayoutConfig>) => onChange({ ...config, ...partial });

  const VARIANT_OPTIONS: Array<{
    value: SidebarVariant;
    label: string;
    description: string;
    preview: React.ReactNode;
  }> = [
    { value: 'sidebar', label: 'Default', description: 'Flush with the page edge', preview: <DefaultPreview /> },
    { value: 'floating', label: 'Floating', description: 'Sidebar floats above content', preview: <FloatingPreview /> },
    { value: 'inset', label: 'Inset', description: 'Inset sidebar with rounded corners', preview: <InsetPreview /> },
  ];

  const COLLAPSIBLE_OPTIONS: Array<{
    value: SidebarCollapsible;
    label: string;
    description: string;
    preview: React.ReactNode;
  }> = [
    { value: 'offcanvas', label: 'Off Canvas', description: 'Slides out of view', preview: <OffCanvasPreview /> },
    { value: 'icon', label: 'Icon', description: 'Collapses to icon-only', preview: <IconPreview /> },
    { value: 'none', label: 'None', description: 'Always fully expanded', preview: <NonePreview /> },
  ];

  const POSITION_OPTIONS: Array<{
    value: SidebarSide;
    label: string;
    preview: React.ReactNode;
  }> = [
    { value: 'left', label: 'Left', preview: <LeftPreview /> },
    { value: 'right', label: 'Right', preview: <RightPreview /> },
  ];

  return (
    <div className="space-y-6">
      {/* ── Sidebar Variant ──────────────────────────────────────────── */}
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium text-foreground">Sidebar Variant</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {VARIANT_OPTIONS.find((o) => o.value === config.sidebarVariant)?.description}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {VARIANT_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              label={opt.label}
              selected={config.sidebarVariant === opt.value}
              onClick={() => update({ sidebarVariant: opt.value })}
              preview={opt.preview}
            />
          ))}
        </div>
      </div>

      <Separator />

      {/* ── Sidebar Collapsible Mode ─────────────────────────────────── */}
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium text-foreground">Sidebar Collapsible Mode</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {COLLAPSIBLE_OPTIONS.find((o) => o.value === config.sidebarCollapsible)?.description}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {COLLAPSIBLE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              label={opt.label}
              selected={config.sidebarCollapsible === opt.value}
              onClick={() => update({ sidebarCollapsible: opt.value })}
              preview={opt.preview}
            />
          ))}
        </div>
      </div>

      <Separator />

      {/* ── Sidebar Position ─────────────────────────────────────────── */}
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium text-foreground">Sidebar Position</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {config.sidebarSide === 'left' ? 'Sidebar positioned on the left side' : 'Sidebar positioned on the right side'}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {POSITION_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              label={opt.label}
              selected={config.sidebarSide === opt.value}
              onClick={() => update({ sidebarSide: opt.value })}
              preview={opt.preview}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
